// Based on https://github.com/thibaudcolas/draftjs-conductor/blob/main/src/lib/api/copypaste.js but
// using our own serialization rather than passing around internal DraftJS objects.
import {
  EditorState,
  Modifier,
  ContentState,
  DraftHandleValue,
  convertToRaw,
  convertFromRaw,
  SelectionState
} from "draft-js";
import type {
  EditorState as EditorStateType,
  RawDraftContentState
} from "draft-js";
// @ts-ignore
import editOnCopy from "draft-js/lib/editOnCopy";
// @ts-ignore
import editOnCut from "draft-js/lib/editOnCut";
// @ts-ignore
import getContentStateFragment from "draft-js/lib/getContentStateFragment";
// @ts-ignore
import getDraftEditorSelection from "draft-js/lib/getDraftEditorSelection";

import { CampaignWithCalleeFields_calleeFields } from "src/graphql/generated/CampaignWithCalleeFields";

import {
  V1CalleeFieldEntityData,
  V2CalleeFieldEntityData,
  convertAnyCalleeFieldToBaseEntity
} from "./richTextConverters";
import {
  serialize,
  deserializePlainText,
  CalleeFieldMatchResult
} from "./serialization";

// Special data- attribute used to copy dialer-specific metadata
const FRAGMENT_ATTR = "data-stw-fragment";

// CSS selector of HTML nodes that are a DraftJS decorator
const DRAFT_DECORATOR = '[data-contents="true"] [contenteditable="false"]';

const MISSING_FIELD = "missing field";
// Checks whether the selection is inside a decorator or not.
// This is important to change the copy-cut behavior accordingly.
const isSelectionInDecorator = (selection: Selection) => {
  const { anchorNode, focusNode } = selection;
  if (!anchorNode || !focusNode) {
    return false;
  }

  const anchor =
    anchorNode instanceof Element ? anchorNode : anchorNode.parentElement;
  const focus =
    focusNode instanceof Element ? focusNode : focusNode.parentElement;

  const anchorDecorator = anchor && anchor.closest(DRAFT_DECORATOR);
  const focusDecorator = focus && focus.closest(DRAFT_DECORATOR);

  return (
    anchorDecorator &&
    focusDecorator &&
    (anchorDecorator.contains(focusDecorator) ||
      focusDecorator.contains(anchorDecorator))
  );
};

// Get clipboard content from the selection like Draft.js would.
const getSelectedContent = (
  editorState: EditorStateType,
  editorRoot: HTMLElement
) => {
  const { selectionState } = getDraftEditorSelection(editorState, editorRoot);

  const fragment = getContentStateFragment(
    editorState.getCurrentContent(),
    selectionState
  );

  // If the selection contains no content (according to Draft.js), use the default browser behavior.
  // This happens when selecting text that's within contenteditable=false blocks in Draft.js.
  // See https://github.com/thibaudcolas/draftjs-conductor/issues/12.
  const isEmpty = fragment.every((block: any) => {
    return block.getText().length === 0;
  });

  return isEmpty ? null : fragment;
};

export function makeCopyPasteHandlers(
  onChangeEditorState: (newState: EditorState) => any,
  calleeFields: CampaignWithCalleeFields_calleeFields[],
  isRichTextEnabled = false
): {
  onCopy: (editor: any, e: React.ClipboardEvent<any>) => void;
  onCut: (editor: any, e: React.ClipboardEvent<any>) => void;
  handlePastedText: (
    text: string,
    html: string | undefined,
    editorState: EditorStateType
  ) => DraftHandleValue;
} {
  // Overrides the default copy/cut behavior, adding Dialer-specific context to the clipboard.
  // See also https://github.com/basecamp/trix/blob/62145978f352b8d971cf009882ba06ca91a16292/src/trix/controllers/input_controller.coffee#L415-L422
  //
  // We serialise the editor content within HTML, not as a separate mime type, because Draft.js only allows access
  // to HTML in its paste event handler. Additionally, some browers' implementation of the copy/paste
  // spec only support plain text and HTML, not custom MIME types.
  const draftEditorCopyCutListener = (
    ref: any,
    e: React.ClipboardEvent<any>
  ) => {
    const selection = window.getSelection();
    if (!selection) {
      return;
    }

    // Completely skip event handling if clipboardData is not supported (IE11 is out).
    // Also skip if there is no selection ranges.
    // Or if the selection is fully within a decorator.
    if (
      !e.clipboardData ||
      selection.rangeCount === 0 ||
      isSelectionInDecorator(selection)
    ) {
      return;
    }

    const fragment = getSelectedContent(ref._latestEditorState, ref.editor);

    // Override the default behavior if there is selected content.
    if (fragment) {
      const content = ContentState.createFromBlockArray(fragment.toArray());

      let rawDraftjsState;
      if (isRichTextEnabled) {
        try {
          rawDraftjsState = convertToRaw(content);
        } catch (_e) {
          // noop, it's a plain text script
        }
      }

      // We copy over the (serialized) copied content. We also include all the
      // callee fields from this campaign, so we can do matching when pasting into
      // a different campaign.
      const serialisedContent = JSON.stringify({
        sourceCampaignCalleeFields: calleeFields,
        serializedScript: serialize(content),
        rawDraftjsState
      });

      // Create a temporary element to store the selection’s HTML.
      // See also Rangy's implementation: https://github.com/timdown/rangy/blob/1e55169d2e4d1d9458c2a87119addf47a8265276/src/core/domrange.js#L515-L520.
      const fragmentElt = document.createElement("div");

      // Modern browsers only support a single range.
      fragmentElt.appendChild(selection.getRangeAt(0).cloneContents());
      fragmentElt.setAttribute(FRAGMENT_ATTR, serialisedContent);

      // We set the style property to replicate the browser's behavior of inline styles in rich text copy-paste.
      // In Draft.js, this is important for line breaks to be interpreted correctly when pasted into another word processor.
      // See https://github.com/facebook/draft-js/blob/a1f4593d8fa949954053e5d5840d33ce1d1082c6/src/component/base/DraftEditor.react.js#L328.
      fragmentElt.setAttribute("style", "white-space: pre-wrap;");

      e.clipboardData.setData("text/plain", selection.toString());
      e.clipboardData.setData("text/html", fragmentElt.outerHTML);

      e.preventDefault();
    }
  };

  const onCopy = (editor: any, e: React.ClipboardEvent<any>): void => {
    draftEditorCopyCutListener(editor, e);
    editOnCopy(editor, e);
  };

  const onCut = (editor: any, e: React.ClipboardEvent<any>): void => {
    draftEditorCopyCutListener(editor, e);
    editOnCut(editor, e);
  };

  /**
   * Returns pasted content coming from Draft.js editors set up to serialise
   * their Draft.js content within the HTML.
   */
  const getDraftEditorPastedContent = (
    html: string | undefined
  ): null | ContentState => {
    // Plain-text pastes handled by DraftJS's default paste handler.
    if (html === "" || typeof html === "undefined" || html === null) {
      return null;
    }

    const doc = new DOMParser().parseFromString(html, "text/html");
    const fragmentElt = doc.querySelector(`[${FRAGMENT_ATTR}]`);

    // Handle the paste if it's coming from the Dialer (as determined by whether
    // it's a HTML element with our special FRAGMENT_ATTR attribute).
    if (!fragmentElt) {
      return null;
    }

    const fragmentAttr = fragmentElt.getAttribute(FRAGMENT_ATTR);
    if (!fragmentAttr) {
      return null;
    }

    try {
      // If JSON parsing or script deserialization fails, leave paste handling to Draft.js.
      // There is no reason for this to happen, unless the clipboard was altered somehow, or
      // edge cases such as copying, then switching to a Dialer that's running a different
      // version of the frontend.
      const { sourceCampaignCalleeFields, serializedScript, rawDraftjsState } =
        JSON.parse(fragmentAttr);

      console.log("Pasted content:", {
        sourceCampaignCalleeFields,
        serializedScript,
        rawDraftjsState
      });

      if (isRichTextEnabled && rawDraftjsState) {
        let draftJsState;
        try {
          draftJsState = convertFromRaw(rawDraftjsState);
        } catch (_e) {
          console.warn("Plain text script encountered in rich text editor.");
        }

        if (draftJsState) {
          return updateCalleeFieldReferences(
            rawDraftjsState,
            calleeFields,
            sourceCampaignCalleeFields
          );
        }
      }

      return deserializePlainText(serializedScript, calleeFields, {
        calleeFieldMatcher: (field, calleeFields) =>
          copyPasteCalleeFieldMatcher(
            field,
            calleeFields,
            sourceCampaignCalleeFields
          )
      });
    } catch (error) {
      console.warn("Failed to parse clipboard content:", error);
      return null;
    }
  };

  const handlePastedText = (
    text: string,
    html: string | undefined,
    editorState: EditorStateType
  ): DraftHandleValue => {
    const pastedContent = getDraftEditorPastedContent(html);

    if (pastedContent) {
      const fragment = pastedContent.getBlockMap();

      const content = Modifier.replaceWithFragment(
        editorState.getCurrentContent(),
        editorState.getSelection(),
        fragment
      );

      onChangeEditorState(
        EditorState.push(editorState, content, "insert-fragment")
      );
      return "handled";
    }

    return "not-handled";
  };

  return { handlePastedText, onCopy, onCut };
}

export function updateCalleeFieldReferences(
  draftJsState: RawDraftContentState,
  calleeFields: CampaignWithCalleeFields_calleeFields[],
  sourceCampaignCalleeFields: CampaignWithCalleeFields_calleeFields[]
): ContentState {
  let content = convertFromRaw(draftJsState);
  const entities = content.getAllEntities();

  let needsPlaceholderReplacement = false;

  entities.forEach((entityInstance, entityKey) => {
    if (!entityInstance || !entityKey) {
      return;
    }

    const entity = entityInstance.getData();

    if (entityInstance.getType() === "CALLEE_FIELD") {
      const matchResult = copyPasteCalleeFieldMatcher(
        entity,
        calleeFields,
        sourceCampaignCalleeFields
      );

      if (matchResult.match) {
        content = content.replaceEntityData(
          entityKey,
          convertAnyCalleeFieldToBaseEntity(matchResult.calleeField)
        );
      } else {
        needsPlaceholderReplacement = true;

        content = content.replaceEntityData(entityKey, {
          isInvalid: true,
          placeholder: matchResult.placeholder
        });
      }
    }
  });

  if (needsPlaceholderReplacement) {
    return replaceMissingFieldsWithPlaceholders(content);
  }

  return content;
}

const replaceMissingFieldsWithPlaceholders = (
  draftJsContent: ContentState
): ContentState => {
  let content = draftJsContent;

  let blocks = content.getBlockMap();
  let blockKey = 0;

  while (blockKey < blocks.size) {
    const block = blocks.get(blocks.keySeq().get(blockKey));
    if (!block) {
      break;
    }
    const blockEntityRanges: { start: number; end: number }[] = [];

    block.findEntityRanges(
      block => block.getEntity() !== null,
      (start, end) => {
        const entity = content.getEntity(block.getEntityAt(start));
        if (
          entity.getType() === "CALLEE_FIELD" &&
          entity.getData().placeholder
        ) {
          blockEntityRanges.push({ start, end });
        }
      }
    );

    if (blockEntityRanges.length === 0) {
      blockKey++;
      continue;
    } else {
      const { start, end } = blockEntityRanges[0];

      const entity = content.getEntity(block.getEntityAt(start));
      const entityData = entity.getData();

      const selection = SelectionState.createEmpty(block.getKey()).merge({
        anchorOffset: start,
        focusOffset: end
      });

      const fieldDisplayString = entityData.placeholder || MISSING_FIELD;

      content = content.createEntity("CALLEE_FIELD", "IMMUTABLE", {
        name: fieldDisplayString,
        isInvalid: true
      });

      content = Modifier.replaceText(
        content,
        selection,
        fieldDisplayString,
        undefined,
        content.getLastCreatedEntityKey()
      );

      blocks = content.getBlockMap();
    }
  }

  return content;
};

export const copyPasteCalleeFieldMatcher = (
  field: string | V1CalleeFieldEntityData | V2CalleeFieldEntityData,
  destinationCampaignCalleeFields: CampaignWithCalleeFields_calleeFields[],
  sourceCampaignCalleeFields: CampaignWithCalleeFields_calleeFields[]
): CalleeFieldMatchResult => {
  // We could be copying either a plain text script or a rich text script,
  // so we need to normalize the callee field data
  const { calleeFieldId, builtInField } =
    convertAnyCalleeFieldToBaseEntity(field);

  // For each callee field in the pasted script, we need to match it up
  // with a callee field in this campaign.
  //
  // If we're copying within a campaign, we should always be able to
  // find a match just based on the ID of the copied field.
  //
  // But if we're copying between campaigns, we want to do a best-effort
  // match: built-in fields should have a match (unless the set of
  // built-in fields has changed), and custom fields should have a match
  // if there's a field in this campaign with the exact same name (mostly
  // so if you copy a campaign, you can copy/paste scripts across them)

  // If it's an unsaved built-in field, match it to the same built-in
  // field in this campaign
  if (!calleeFieldId && builtInField) {
    const calleeField = destinationCampaignCalleeFields.find(
      ({ builtInField: builtInFieldName }) => builtInFieldName === builtInField
    );

    if (calleeField) {
      return { match: true, calleeField };
    } else {
      return { match: false, placeholder: builtInField };
    }
  }

  // try to find a field with the same id (for when we're copying within
  // a campaign)
  const idMatch =
    calleeFieldId &&
    destinationCampaignCalleeFields.find(field => field.id === calleeFieldId);
  if (idMatch) {
    return { match: true, calleeField: idMatch };
  }

  // We're probably copying across campaigns. Get details on the field
  // from the source campaign and try to find a matching field from the
  // destination campaign.
  const sourceField =
    calleeFieldId &&
    sourceCampaignCalleeFields.find(
      (sourceField: CampaignWithCalleeFields_calleeFields) =>
        sourceField.id === calleeFieldId
    );
  if (!sourceField) {
    return { match: false, placeholder: MISSING_FIELD };
  }

  if (sourceField.builtInField) {
    // For built-in fields, match on the built-in field name
    const matchingBuiltInField = destinationCampaignCalleeFields.find(
      ({ builtInField }) => builtInField === sourceField.builtInField
    );
    if (matchingBuiltInField) {
      return { match: true, calleeField: matchingBuiltInField };
    }
  }

  // not a built-in field, try to find a custom field with the exact
  // same name
  const matchingNameField = destinationCampaignCalleeFields.find(
    ({ name }) =>
      name.trim().localeCompare(sourceField.name.trim(), undefined, {
        sensitivity: "base"
      }) === 0
  );

  if (matchingNameField) {
    return { match: true, calleeField: matchingNameField };
  }

  // No match -- strip out the custom field but use the name as a plaintext
  // placeholder.

  // Note: If the user pastes a script, then copies+pastes again,
  // the source callee field ID will have been stripped out, so it will be displayed as "missing field"
  return {
    match: false,
    placeholder: MISSING_FIELD
  };
};
